/**
 * The metrics band: the three plant metrics (which are also the navigation)
 * and, on the right, the one brick CTA the active screen has — Allocate on
 * View 1, Publish on View 2.
 */
import React, { useCallback } from "react";

import { BoardScreen } from "../types";
import { BoardMetrics } from "../utils/derive";
import { eyebrowSx, fmtHours, lbb } from "../utils/tokens";
import Metric, { MetricDivider } from "./metric";

export interface MetricsBandProps {
	screen: BoardScreen;
	onScreen: (screen: BoardScreen) => void;
	isWeek: boolean;
	metrics: BoardMetrics;
	/** From the skill-gap board, so the headline can never disagree with its footer. */
	gapSummary: { coveredPct: number; uncoveredHours: number };
	gapRowCount: number;
	/** "8h 30m" — the cap the legend names. */
	capLabel: string;
	onAllocate?: () => void;
	allocating?: boolean;
	/** A manual edit is saving; the button waits without claiming to allocate. */
	busy?: boolean;
	/** Placements drafted on the board; Allocate saves them first. */
	pendingPins?: number;
	draftCount: number;
	published: boolean;
	publishing: boolean;
	onPublish: () => void;
}

const MetricsBand: React.FC<MetricsBandProps> = ({
	screen,
	onScreen,
	isWeek,
	metrics,
	gapSummary,
	gapRowCount,
	capLabel,
	onAllocate,
	allocating,
	busy = false,
	pendingPins = 0,
	draftCount,
	published,
	publishing,
	onPublish,
}) => {
	const showUtil = useCallback(() => onScreen("util"), [onScreen]);
	const showGaps = useCallback(() => onScreen("gaps"), [onScreen]);
	const showFlex = useCallback(() => onScreen("flex"), [onScreen]);
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 16,
				padding: "6px 16px",
				background: lbb.alloyWarm,
				borderBottom: lbb.borderInk,
				flexWrap: "wrap",
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
				<Metric
					title="Labor utilization"
					value={`${metrics.utilPct}%`}
					sub={metrics.utilSub}
					color={metrics.utilPct > 95 ? lbb.brick : metrics.utilPct < 70 ? lbb.warning : lbb.navy}
					active={screen === "util"}
					onClick={showUtil}
				/>
				<MetricDivider />
				<Metric
					title="Order demand covered"
					value={`${gapSummary.coveredPct}%`}
					sub={
						gapSummary.uncoveredHours > 0
							? `${Math.round(gapSummary.uncoveredHours)} worker-hours short across ${gapRowCount} lines`
							: "every block covered on this shift"
					}
					color={gapSummary.coveredPct < 98 ? lbb.brick : lbb.success}
					active={screen === "gaps"}
					onClick={showGaps}
				/>
				<MetricDivider />
				<Metric
					title="Overtime / flex exposure"
					value={metrics.overtimeHours > 0 ? fmtHours(metrics.overtimeHours) : "None"}
					sub={
						metrics.overtimeHours > 0
							? `${metrics.overtimeHeads} worker${metrics.overtimeHeads > 1 ? "s" : ""} past their ${isWeek ? "week" : "shift"} (${capLabel})`
							: `nobody past their ${isWeek ? "week" : "shift"} (${capLabel})`
					}
					color={metrics.overtimeHours > 0 ? lbb.brick : lbb.success}
					active={screen === "flex"}
					onClick={showFlex}
				/>
			</div>

			{/* Right cluster is view-dependent, and only ever ONE brick CTA. */}
			{screen === "util" && (
				<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
					{pendingPins > 0 && (
						<div
							style={{ display: "flex", alignItems: "baseline", gap: 6 }}
							title={pendingPins === 1 ? "1 placement, saved when you fill" : `${pendingPins} placements, saved when you fill`}
						>
							<div style={{ ...eyebrowSx, color: lbb.ink60 }}>Drafted</div>
							<div style={{ fontFamily: lbb.fontMono, fontSize: 16, fontWeight: 700, color: lbb.brick, lineHeight: 1 }}>{pendingPins}</div>
						</div>
					)}
					<button
						type="button"
						onClick={onAllocate}
						disabled={allocating || busy}
						style={{
							fontFamily: lbb.fontUi,
							fontSize: 13,
							fontWeight: 700,
							color: lbb.paper,
							background: allocating || busy ? lbb.brickDeep : lbb.brick,
							border: lbb.borderInk,
							borderRadius: 4,
							padding: "7px 16px",
							cursor: allocating || busy ? "progress" : "pointer",
						}}
					>
						{allocating ? "Filling…" : busy ? "Saving…" : isWeek ? (pendingPins > 0 ? `Save ${pendingPins} drafted · fill open slots` : "Fill open slots · week") : "Fill open slots"}
					</button>
				</div>
			)}

			{screen === "gaps" && (
				<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
					<div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
						<div style={{ ...eyebrowSx, color: lbb.ink60 }}>Cover drafted</div>
						<div
							style={{
								fontFamily: lbb.fontMono,
								fontSize: 16,
								fontWeight: 700,
								color: lbb.navy,
								fontVariantNumeric: "tabular-nums",
							}}
						>
							{draftCount}
						</div>
					</div>
					<button
						type="button"
						onClick={onPublish}
						disabled={draftCount === 0 || published || publishing}
						style={{
							fontFamily: lbb.fontUi,
							fontSize: 13,
							fontWeight: 700,
							border: lbb.borderInk,
							borderRadius: 4,
							padding: "7px 16px",
							cursor: draftCount === 0 || published ? "default" : "pointer",
							color: published || draftCount === 0 ? lbb.navy : lbb.paper,
							background: published ? lbb.alloy : draftCount === 0 ? lbb.paper : lbb.brick,
						}}
					>
						{published
							? "Published · tracking fill"
							: publishing
								? "Publishing…"
								: `Publish ${draftCount} request${draftCount === 1 ? "" : "s"}`}
					</button>
				</div>
			)}
		</div>
	);
};

export default MetricsBand;
